import { useCallback, useId, useLayoutEffect, useRef, type ReactNode, type PointerEvent } from 'react'
import { createPortal } from 'react-dom'
import { Dialog as DSDialog, Heading } from '@digdir/designsystemet-react'
import { Button } from './Button'
import { handleDialogKeyDown, restoreDialogFocus } from './dialogFocus'
import { useT } from '@/shared/i18n'

export interface SlideOverProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  children: ReactNode
  footer?: ReactNode
  side?: 'right' | 'left'
  size?: 'default' | 'wide' | 'workspace'
}

function isOutside(event: PointerEvent<HTMLDialogElement>) {
  const { left, right, top, bottom } = event.currentTarget.getBoundingClientRect()
  return event.target === event.currentTarget &&
    (event.clientX < left || event.clientX > right || event.clientY < top || event.clientY > bottom)
}

export function SlideOver({
  open,
  onClose,
  title,
  children,
  footer,
  side = 'right',
  size = 'default',
}: SlideOverProps) {
  const t = useT()
  const titleId = useId()
  const panelRef = useRef<HTMLDialogElement>(null)
  useLayoutEffect(() => restoreDialogFocus(panelRef.current))
  const outsidePointer = useRef<number | null>(null)

  // Close before the portal node is removed so native focus restoration still runs.
  // A native close is cleanup, never another request to the owner.
  const dialogRef = useCallback((dialog: HTMLDialogElement | null) => {
    panelRef.current = dialog
    if (!dialog) return
    return () => {
      if (dialog.open) dialog.close()
      panelRef.current = null
      outsidePointer.current = null
    }
  }, [])

  if (!open) return null
  const hasTitle = title != null && typeof title !== 'boolean' && (typeof title !== 'string' || title.trim().length > 0)

  // Keep overlays outside the shell's text zoom, as before. Native modality owns
  // the top layer, initial focus, Tab navigation and background inertness;
  // DS's body:has(.ds-dialog:modal) rule locks scrolling, including nested panels.
  return createPortal(
    <DSDialog
      ref={dialogRef}
      open={open}
      modal
      placement={side}
      closedby="closerequest"
      closeButton={false}
      className={`modal modal--drawer${size === 'default' ? '' : ` modal--drawer-${size}`}`}
      data-side={side}
      aria-modal="true"
      aria-labelledby={hasTitle ? titleId : undefined}
      aria-label={hasTitle ? undefined : t('chrome.dialog.panel_label')}
      onKeyDown={event => handleDialogKeyDown(event, onClose)}
      style={size === 'workspace' ? undefined : {
        height: '100%',
        maxHeight: '100%',
        ...(size === 'wide' ? { width: '94vw', maxWidth: 1400, borderRadius: 0 } : {}),
      }}
      onCancel={(event) => {
        if (event.target !== event.currentTarget) return
        event.preventDefault()
        event.stopPropagation()
        onClose()
      }}
      onPointerDown={(event) => {
        outsidePointer.current = event.button === 0 && isOutside(event) ? event.pointerId : null
      }}
      onPointerUp={(event) => {
        const dismiss = outsidePointer.current === event.pointerId && isOutside(event)
        outsidePointer.current = null
        if (dismiss) {
          event.stopPropagation()
          onClose()
        }
      }}
      onPointerCancel={() => { outsidePointer.current = null }}
    >
      <DSDialog.Block className="modal__hd">
        {hasTitle ? <Heading level={2} id={titleId} className="modal__title">{title}</Heading> : <span />}
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          icon="x"
          aria-label={t('chrome.actions.close')}
          onClick={onClose}
        />
      </DSDialog.Block>
      <DSDialog.Block className="modal__body">{children}</DSDialog.Block>
      {footer !== undefined && <DSDialog.Block className="modal__ft">{footer}</DSDialog.Block>}
    </DSDialog>,
    document.body,
  )
}
